import { useQuery } from '@tanstack/react-query';
import { fetchEngagementSnapshots } from '../services/outreach/engagementAnalyticsService';
import {
  EngagementTimeRange,
  getClickedTimestamp,
  getOpenedTimestamp,
  getDaysFromRange,
  isWithinRange,
  roundToTwo,
} from '../utils/engagementAnalytics';

export type HeatmapMatrix = Record<number, Record<number, number>>;

const DAY_MS = 24 * 60 * 60 * 1000;

const initializeHeatmap = (): HeatmapMatrix => {
  const matrix: HeatmapMatrix = {};
  for (let day = 0; day < 7; day += 1) {
    matrix[day] = {};
    for (let hour = 0; hour < 24; hour += 1) {
      matrix[day][hour] = 0;
    }
  }
  return matrix;
};

const buildHeatmap = async (
  timeRange: EngagementTimeRange
): Promise<HeatmapMatrix> => {
  const snapshots = await fetchEngagementSnapshots({
    includeStats: false,
    includeTracking: true,
  });

  const matrix = initializeHeatmap();
  const records = snapshots.flatMap((snapshot) => snapshot.tracking ?? []);
  if (!records.length) {
    return matrix;
  }

  const now = Date.now();
  const days = getDaysFromRange(timeRange);
  const start = now - days * DAY_MS;

  records.forEach((record) => {
    const openedTimestamp = getOpenedTimestamp(record);
    if (isWithinRange(openedTimestamp, start, now)) {
      const openedDate = new Date(openedTimestamp!);
      matrix[openedDate.getDay()][openedDate.getHours()] += 1;
    }

    const clickedTimestamp = getClickedTimestamp(record);
    if (isWithinRange(clickedTimestamp, start, now)) {
      const clickedDate = new Date(clickedTimestamp!);
      matrix[clickedDate.getDay()][clickedDate.getHours()] += 1;
    }
  });

  // Round to avoid floating point noise if any downstream averaging occurs
  Object.values(matrix).forEach((row) => {
    Object.keys(row).forEach((hourKey) => {
      row[Number(hourKey)] = roundToTwo(row[Number(hourKey)]);
    });
  });

  return matrix;
};

export const useEngagementHeatmapData = (
  timeRange: EngagementTimeRange = '30d'
) =>
  useQuery<HeatmapMatrix>({
    queryKey: ['engagement-heatmap', timeRange],
    queryFn: () => buildHeatmap(timeRange),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    placeholderData: (previousData) => previousData ?? initializeHeatmap(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
